package com.swetdot.swetgram.data.auth

import com.swetdot.swetgram.protocol.dto.AuthSessionResponse
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.time.Clock

/**
 * Owns the access token and its refresh.
 *
 * The interesting part is the [Mutex]. On app resume several screens fire requests at once and
 * all of them can see an expired token together. Without serialising, each would refresh
 * independently -- and because the contract ROTATES refresh tokens and treats a replayed one as
 * theft (`refresh_token_reuse_detected`), the second refresh would present an already-spent
 * token and the server would revoke the whole session. The user would be silently signed out
 * for doing nothing but opening the app.
 *
 * So: one refresh at a time, and whoever arrives during a refresh in flight waits for it and
 * reuses the result.
 */
class SessionManager(
    private val store: SessionStorage,
    private val now: () -> Long = { Clock.System.now().toEpochMilliseconds() },
) {
    private val mutex = Mutex()

    private val _state = MutableStateFlow(store.load())
    val state: StateFlow<StoredSession?> = _state.asStateFlow()

    val isSignedIn: Boolean get() = _state.value != null
    val currentUserId: String? get() = _state.value?.userId

    /**
     * Refresh this far before actual expiry. A token that expires while a request is in flight
     * produces a spurious 401; a small skew window costs nothing and removes that class of
     * failure entirely.
     */
    private val expirySkewMs = 30_000L

    fun accessTokenOrNull(): String? = _state.value?.accessToken

    fun needsRefresh(): Boolean {
        val session = _state.value ?: return false
        return now() >= session.accessExpiresAtMs - expirySkewMs
    }

    /**
     * Stores a fresh session. [AuthSessionResponse.tokens] carries TTLs in SECONDS, so the
     * absolute expiry is computed here, once, from the moment of receipt.
     */
    fun adopt(response: AuthSessionResponse) {
        val session = StoredSession(
            accessToken = response.tokens.accessToken,
            refreshToken = response.tokens.refreshToken,
            accessExpiresAtMs = now() + response.tokens.accessExpiresIn * 1000,
            userId = response.user.id,
            emailVerified = response.user.emailVerified,
            email = response.user.email,
        )
        store.save(session)
        _state.value = session
    }

    /**
     * Called after the server accepts a verification code.
     *
     * The flag is flipped locally rather than by refetching the profile: the 204 IS the
     * server's confirmation, and a round trip here would leave the user staring at the code
     * screen for no reason.
     */
    fun markEmailVerified() {
        val current = _state.value ?: return
        if (current.emailVerified) return
        val updated = current.copy(emailVerified = true)
        store.save(updated)
        _state.value = updated
    }

    fun signOut() {
        store.clear()
        _state.value = null
    }

    /**
     * Runs [refresh] under the lock, but only if someone else has not already done it.
     *
     * The re-check inside the lock is what makes the crowd case correct: three callers queue,
     * the first refreshes, and the other two find a valid token and return immediately instead
     * of presenting the refresh token that was just rotated away.
     */
    suspend fun refreshIfNeeded(refresh: suspend (refreshToken: String) -> AuthSessionResponse): String? {
        if (!needsRefresh()) return accessTokenOrNull()

        return mutex.withLock {
            if (!needsRefresh()) return@withLock accessTokenOrNull()

            val current = _state.value ?: return@withLock null
            val response = refresh(current.refreshToken)
            adopt(response)
            accessTokenOrNull()
        }
    }
}
